import React from 'react';
import Layout from '../components/Layout';

const Shipping: React.FC = () => {
  return (
    <Layout title="Shipping & Delivery Policy" subtitle="Effective Date: 28 August 2024">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Introduction */}
        <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
          <p className="text-white/90 leading-relaxed">
            Incupie Technologies Private Limited provides digital AI services and software solutions. 
            Since our products are digital in nature, traditional shipping does not apply. This policy 
            outlines how our digital products and services are delivered to you.
          </p>
        </div>

        {/* Policy Sections */}
        <div className="space-y-6">
          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. Digital Product Delivery</h2>
            <p className="text-white/90 mb-4">
              All Incupie products and services are delivered digitally. Upon successful payment 
              and account verification:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li>Access to your subscribed services is granted immediately</li>
              <li>Download links and installation instructions are provided via email</li>
              <li>Account credentials and setup information are sent to your registered email address</li>
              <li>You will receive a welcome email with getting started resources</li>
            </ul>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. Service Activation Timeline</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Immediate Access Services</h3>
                <p className="text-white/80">
                  Most of our AI platform features are available immediately after payment confirmation:
                </p>
                <ul className="list-disc list-inside text-white/70 space-y-1 ml-4 mt-2">
                  <li>Basic AI model access</li>
                  <li>Standard compression tools</li>
                  <li>Community support access</li>
                  <li>Documentation and tutorials</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Custom Setup Services</h3>
                <p className="text-white/80">
                  Some premium features may require additional setup time:
                </p>
                <ul className="list-disc list-inside text-white/70 space-y-1 ml-4 mt-2">
                  <li>Enterprise integrations: 1-3 business days</li>
                  <li>Custom model training: 3-7 business days</li>
                  <li>Dedicated support channels: 1-2 business days</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. Software Downloads</h2>
            <p className="text-white/90 mb-4">
              For downloadable software components:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li>Download links are valid for 30 days from the date of purchase</li>
              <li>Multiple downloads are allowed during the validity period</li>
              <li>Software is available for Windows, macOS, and Linux platforms</li>
              <li>Installation guides and system requirements are provided</li>
              <li>Updates and patches are delivered automatically through the platform</li>
            </ul>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. Email Delivery</h2>
            <p className="text-white/90 mb-4">
              Important information is delivered via email. Please ensure:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4">
              <li>Your email address is accurate and up to date</li>
              <li>Check your spam/junk folder if you don't receive emails within 30 minutes</li>
              <li>Add <strong className="text-white">noreply@incupie.com</strong> and <strong className="text-white">support@incupie.com</strong> to your safe sender list</li>
              <li>Contact support if you haven't received access information within 2 hours</li>
            </ul>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Account Access Issues</h2>
            <p className="text-white/90 mb-4">
              If you experience issues accessing your services:
            </p>
            <ol className="list-decimal list-inside text-white/80 space-y-2 ml-4">
              <li>Verify your payment was processed successfully</li>
              <li>Check your email for account activation instructions</li>
              <li>Clear your browser cache and cookies</li>
              <li>Try accessing from a different device or browser</li>
              <li>Contact our support team if issues persist</li>
            </ol>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Technical Requirements</h2>
            <p className="text-white/90 mb-4">
              To ensure smooth delivery and operation of our services:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Minimum System Requirements</h3>
                <ul className="list-disc list-inside text-white/70 space-y-1 ml-4">
                  <li>Internet connection: 10 Mbps or higher</li>
                  <li>Modern web browser (Chrome, Firefox, Safari, Edge)</li>
                  <li>Operating System: Windows 10+, macOS 10.14+, Ubuntu 18.04+</li>
                  <li>RAM: 8GB minimum, 16GB recommended</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Recommended for Optimal Performance</h3>
                <ul className="list-disc list-inside text-white/70 space-y-1 ml-4">
                  <li>High-speed broadband connection</li>
                  <li>Latest browser versions</li>
                  <li>SSD storage for faster processing</li>
                  <li>Dedicated GPU for AI model processing</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-dark-800/50 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Service Interruptions</h2>
            <p className="text-white/90">
              In the rare event of service interruptions during delivery or setup, we will:
            </p>
            <ul className="list-disc list-inside text-white/80 space-y-2 ml-4 mt-4">
              <li>Notify affected users via email and status page updates</li>
              <li>Provide estimated resolution times</li>
              <li>Extend service periods to compensate for downtime</li>
              <li>Offer alternative access methods when possible</li>
            </ul>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-primary-500/10 to-primary-600/10 border border-primary-500/20 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white mb-4">Delivery Support</h2>
          <p className="text-white/90 mb-4">
            Need help with accessing your services or have questions about delivery?
          </p>
          <div className="space-y-2 text-white/90">
            <p><strong className="text-white">Email:</strong>{' '}
              <a href="mailto:support@incupie.com" className="text-primary-400 hover:text-primary-300 transition-colors">
                support@incupie.com
              </a>
            </p>
            <p><strong className="text-white">Phone:</strong> +91-9915080818</p>
            <p><strong className="text-white">Support Hours:</strong> Monday - Friday, 9:00 AM to 6:00 PM IST</p>
            <p><strong className="text-white">Emergency Support:</strong> Available 24/7 for critical access issues</p>
            <p><strong className="text-white">Discord Community:</strong>{' '}
              <a href="https://discord.gg/7WsMfhweUs" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 transition-colors">
                Join our Discord
              </a>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Shipping;
